// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/common/backendFrontend/composeTemplateDocumentBe.ts

import { unwrapSelectionMarkup } from './unwrapSelectionMarkupFe';
import { getDropdownCommentMark } from './dropdownCommentValue';

/**
 * Compose a bound document by merging template HTML with field values.
 *
 * Finds all template field nodes (inline, block, dropdown) in the template HTML
 * and replaces their content with the corresponding field values.
 * Dropdown fields use textContent (plain text); inline/block fields use innerHTML.
 * Fields with no matching value keep the template's initial content (e.g. new field added since creation).
 *
 * Strips template author-side selection markup BEFORE substitution, so template comment marks do
 * not leak into bound instances while comment marks inside field values (substituted after) survive.
 */
export const composeTemplateDocument = (templateHtml: string, fieldValues: Record<string, string>): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${templateHtml}</body>`, 'text/html');

  unwrapSelectionMarkup(doc);

  doc.querySelectorAll('template-field-inline, template-field-block, template-field-dropdown').forEach((el) => {
    const fieldId = el.getAttribute('data-field-id');
    if (!fieldId) return;
    const value = fieldValues[fieldId];
    if (value === undefined) return;
    // Dropdown values are plain text - keep them escaped so a value can't become live HTML.
    if (el.tagName === 'TEMPLATE-FIELD-DROPDOWN') {
      const commentMark = getDropdownCommentMark(value);
      if (commentMark) {
        el.textContent = commentMark.text;
        const wrapper = doc.createElement('selection-mark');
        wrapper.setAttribute('data-comment-id', commentMark.commentId);
        el.replaceWith(wrapper);
        wrapper.appendChild(el);
      } else {
        el.textContent = value;
      }
    } else el.innerHTML = value;
  });

  return doc.body.innerHTML;
};
